<script lang="ts">
  import RangeSlider from "svelte-range-slider-pips";
  import Card from "../atoms/Card.svelte";
  import EstimationIcons from "../icons/EstimationIcons.svelte";
  import NumberInput from "../atoms/NumberInput.svelte";
  import Text from "../atoms/Text.svelte";
  import Radio from "../atoms/Radio.svelte";
  import type { ResidenceTypes } from "../../helpers/types";
  import { useDebounce } from "../../helpers/hooks";
  import ErrorText from "../atoms/ErrorText.svelte";

  interface Props {
    inputEstimationValue: number;
    residenceType: ResidenceTypes | undefined;
    residenceText?: string;
    hasPressedContinue?: boolean;
    children?: import("svelte").Snippet;
  }

  let {
    inputEstimationValue = $bindable(),
    residenceType = $bindable(),
    residenceText = "Jag bor i ",
    hasPressedContinue = $bindable(false),
    children,
  }: Props = $props();

  const getEnergyEstimationFactor = () => {
    return residenceType === "apartment" ? 2500 / 65 : 100;
  };

  const toKwh = (sqm: number) => Math.round(sqm * getEnergyEstimationFactor());
  
  // Square-meter range the slider can represent. The slider library is
  // Svelte-3-era: under Svelte 5 legacy compat its internal `$:` block — which
  // owns value clamping AND the handle's on-screen spring position — does not
  // re-run on fine-grained mutations like `values[0] = x`, so the handle can
  // freeze in place (or keep out-of-range values) while everything else
  // updates. Two rules keep it alive:
  //   1. always REASSIGN the whole array (`values = [x]`), never mutate;
  //   2. only hand it values already clamped to this range and rounded to the
  //      step grid, so it never writes a corrected array back to us.
  const sliderMin = $derived(residenceType === "house" ? 60 : 10);
  const sliderMax = $derived(residenceType === "house" ? 300 : 250);
  const toSliderValue = (kwh: number) =>
    Math.min(sliderMax, Math.max(sliderMin, Math.round(kwh / getEnergyEstimationFactor())));
  let values = $state([toSliderValue(inputEstimationValue)]);

  const getTooltipclass = (): Element | null => {
    return document.querySelector(".rangeFloat");
  };

  const hideSquareMeters = useDebounce(() => {
    const tooltip = getTooltipclass();
    if (!tooltip) return;
    tooltip.classList.add("fade-out");
  }, 1500);

  const onDrag = (e: CustomEvent<{ value: number }>) => {
    inputEstimationValue = toKwh(e.detail.value);
    const tooltip = getTooltipclass();
    if (!tooltip) return;
    tooltip.classList.remove("fade-out");
    hideSquareMeters();
  };

  const onNumberInput = (numberInput: Event) => {
    const value = (numberInput.target as HTMLInputElement | null)?.value;
    if (value == null || !Number.isFinite(Number(value))) return;
    values = [toSliderValue(Number(value))];
    getTooltipclass()?.classList.add("fade-out");
  };

  const getLabel = (e: number) => {
    const label = residenceType === "apartment" ? "Lägenhet" : "Villa";

    return `${label} ${e} kvm`;
  };

  const onResidenceChange = (type: ResidenceTypes) => {
    residenceType = type;
    if (type === "apartment") {
      const apartmentValue = 65;
      values = [apartmentValue];
      inputEstimationValue = toKwh(apartmentValue);
    } else {
      const houseValue = 150;
      values = [houseValue];
      inputEstimationValue = toKwh(houseValue);
    }
    const tooltip = getTooltipclass();
    if (tooltip) tooltip.classList.remove("fade-out");
  };

  const showResidenceError = $derived(!residenceType && hasPressedContinue);

  // Cap at the slider's largest representable value: house max, 300 sqm × 100.
  $effect(() => {
    if (inputEstimationValue > 30000) {
      inputEstimationValue = 30000;
    }
  });
</script>

<Card>
  {@render children?.()}
  <!-- aria-label instead of an sr-only <legend>: avoids WebKit's fieldset+legend
       phantom-height bug (Safari-only). See AddressFields.svelte. -->
  <fieldset
    class="border-none p-0 m-0"
    aria-label="Bostadstyp"
    aria-describedby={showResidenceError ? "residence-type-error" : undefined}
  >
    {#if showResidenceError}
      <ErrorText id="residence-type-error">Vänligen välj bostadstyp för att fortsätta.</ErrorText>
    {/if}
      <div class="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2">
        <Text class="whitespace-nowrap mr-4" variant="primary">{residenceText}</Text>

        <div class="flex gap-x-4">
          <Radio
            required={!residenceType}
            name="residenceType"
            group={residenceType}
            value="apartment"
            onChange={() => onResidenceChange("apartment")}>Lägenhet</Radio
          >
          <Radio
            required={!residenceType}
            name="residenceType"
            group={residenceType}
            value="house"
            onChange={() => onResidenceChange("house")}>Villa</Radio
          >
        </div>
      </div>
  </fieldset>
  <div
    class="transition-maxHeight {residenceType
      ? 'max-h-96 duration-700'
      : 'max-h-0  duration-300'} overflow-hidden"
  >
    <div class="flex fade-in flex-col-reverse md:flex-row-reverse mt-12">
      <div class="mt-6 md:mt-0 self-start items-center">
        <NumberInput
          onInput={onNumberInput}
          class="w-20"
          name="estimation"
          id="estimation"
          bind:value={inputEstimationValue}
          max={30000}
        />
        <label for="estimation" class="text-Neutral900 text-M ml-2">kWh/år</label>
      </div>
      <div class="w-full mt-3 notranslate">
        <RangeSlider
          on:change={onDrag}
          id="slider"
          min={sliderMin}
          step={1}
          formatter={(v: any) => getLabel(v)}
          max={sliderMax}
          bind:values
          float
        />
        <EstimationIcons {residenceType} />
      </div>
    </div>
  </div>
</Card>
